import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const QuizResult = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { slug, encoded_id } = useParams();

    const [name, setName] = useState('');
    const [downloading, setDownloading] = useState(false);

    const state = location.state;

    useEffect(() => {
        if (!state || !state.quiz) {
            navigate('/quizzes');
        }
    }, [state, navigate]);

    if (!state || !state.quiz) return null;

    const { quiz, score, totalQuestions } = state;
    const isPassed = score >= 50;

    const handleDownloadCertificate = async () => {
        if (!name.trim()) {
            toast.error("Please enter your name for the certificate.");
            return;
        }

        setDownloading(true);
        try {
            const payload = {
                name: name,
                score: score,
                quiz_id: quiz.id
            };
            
            const response = await fetch(`${process.env.REACT_APP_SERVER}/api/quiz/certificate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error("Failed to generate certificate");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${quiz.title}_Certificate.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success("Certificate downloaded successfully!");
        } catch (error) {
            console.error("Error downloading certificate:", error);
            toast.error("Failed to download certificate. Please try again.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <section className="px-2" style={{ marginTop: '50px', marginBottom: '50px', minHeight: '60vh' }}>
            <div className="container">
                <div className="row">
                    
                    {/* Left Panel: Score Summary (Matches TakeQuiz.js left panel) */}
                    <div className="col-lg-3 col-md-4 mb-4">
                        <div className="card shadow-sm border-0 p-3" style={{ borderRadius: '10px' }}>
                            <h6 className="mb-3" style={{ fontWeight: 'bold' }}>Quiz Result</h6>
                            
                            <div className="text-center mb-4 mt-3">
                                <h1 style={{ fontSize: '54px', fontWeight: 'bold', color: isPassed ? '#198754' : '#dc3545', margin: 0, lineHeight: '1' }}>
                                    {score}%
                                </h1>
                                <p className="text-muted mt-2" style={{ fontSize: '14px', marginBottom: '15px' }}>Final Score</p>
                                
                                <span className={`badge ${isPassed ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '12px', padding: '8px 16px', borderRadius: '20px', letterSpacing: '1px' }}>
                                    {isPassed ? 'PASSED' : 'FAILED'}
                                </span>
                            </div>

                            <div className="border-top pt-3 mb-4">
                                <div className="d-flex justify-content-between mb-2" style={{ fontSize: '14px' }}>
                                    <span className="text-muted">Total Questions:</span>
                                    <span style={{ fontWeight: 'bold', color: '#333' }}>{totalQuestions}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2" style={{ fontSize: '14px' }}>
                                    <span className="text-muted">Passing Score:</span>
                                    <span style={{ fontWeight: 'bold', color: '#333' }}>50%</span>
                                </div>
                            </div>

                            <button 
                                className="btn btn_theme w-100 py-2 mb-2" 
                                style={{ borderRadius: '5px', fontSize: '14px', fontWeight: 'bold' }}
                                onClick={() => navigate(`/quiz/${slug}/${encoded_id}`)}
                            >
                                <i className="fa fa-repeat me-2"></i> Retake Quiz
                            </button>

                            <button 
                                className="btn btn-outline-secondary w-100 py-2" 
                                style={{ borderRadius: '5px', fontSize: '14px', fontWeight: 'bold' }}
                                onClick={() => navigate('/quizzes')}
                            >
                                <i className="fa fa-arrow-left me-2"></i> All Quizzes
                            </button>
                        </div>
                    </div>

                    {/* Right Panel: Content / Certificate (Matches TakeQuiz.js right panel) */}
                    <div className="col-lg-9 col-md-8 mb-4">
                        <div className="card shadow-sm border-0 p-4" style={{ borderRadius: '10px', minHeight: '100%' }}>
                            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                                <h5 className="mb-0" style={{ fontWeight: 'bold', color: '#333', lineHeight: '1.4' }}>
                                    {quiz.title}
                                </h5>
                            </div>

                            {isPassed ? (
                                <div className="row align-items-center">
                                    <div className="col-lg-6 col-md-12 mb-4 mb-lg-0 pe-lg-4">
                                        <h5 className="mb-3" style={{ fontWeight: 'bold', color: '#198754' }}>
                                            <i className="fa fa-trophy me-2" style={{ fontSize: '20px' }}></i> 
                                            Congratulations!
                                        </h5>
                                        <p className="text-muted" style={{ fontSize: '15px', lineHeight: '1.6' }}>
                                            You have successfully passed the assessment and are eligible for a certificate of completion. 
                                            Please enter your name below exactly as you would like it to appear on your official document.
                                        </p>
                                        
                                        <div className="form-group mt-4 mb-4">
                                            <label className="mb-2" style={{ fontWeight: 'bold', fontSize: '14px', color: '#555' }}>Name for Certificate</label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                placeholder="e.g. Rahul Sharma" 
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                style={{ borderRadius: '5px', padding: '12px 15px', border: '1px solid #ced4da' }}
                                            />
                                        </div>

                                        <button 
                                            className="btn btn_theme w-100 py-2" 
                                            onClick={handleDownloadCertificate}
                                            disabled={downloading || !name.trim()}
                                            style={{ borderRadius: '5px', fontWeight: 'bold', fontSize: '15px' }}
                                        >
                                            {downloading ? (
                                                <><i className="fa fa-spinner fa-spin me-2"></i> Generating...</>
                                            ) : (
                                                <><i className="fa fa-download me-2"></i> Download Certificate</>
                                            )}
                                        </button>
                                    </div>
                                    
                                    <div className="col-lg-6 col-md-12">
                                        <div className="p-3" style={{ background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                            <h6 className="text-muted text-center mb-3" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 'bold' }}>Certificate Preview</h6>
                                            <div className="certificate-mockup" style={{ 
                                                background: '#fff', 
                                                border: '6px solid #C8A951', 
                                                padding: '2px', 
                                                borderRadius: '4px',
                                                boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                                position: 'relative'
                                            }}>
                                                <div style={{ border: '1px solid #eee', padding: '30px 15px', textAlign: 'center', minHeight: '220px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                    <h4 style={{ fontFamily: 'Georgia, serif', color: '#111', margin: '0 0 5px 0', fontSize: '18px', letterSpacing: '1px' }}>CERTIFICATE</h4>
                                                    <p style={{ fontFamily: 'Georgia, serif', fontSize: '9px', margin: '0 0 20px 0', letterSpacing: '2px', color: '#666' }}>OF ACHIEVEMENT</p>
                                                    
                                                    <p style={{ fontStyle: 'italic', fontSize: '11px', margin: '0 0 10px 0', color: '#777' }}>Proudly Presented To</p>
                                                    
                                                    <h3 style={{ fontFamily: '"Brush Script MT", cursive, serif', fontSize: '28px', color: '#1e2820', margin: '0 0 10px 0', borderBottom: '1px solid #C8A951', display: 'inline-block', padding: '0 20px', minWidth: '180px' }}>
                                                        {name || 'Your Name'}
                                                    </h3>
                                                    
                                                    <p style={{ fontSize: '11px', color: '#555', marginTop: '10px', lineHeight: '1.4' }}>
                                                        For successfully completing<br/><strong>{quiz.title}</strong>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <div style={{ fontSize: '50px', color: '#dc3545', marginBottom: '15px' }}>
                                        <i className="fa fa-exclamation-circle"></i>
                                    </div>
                                    <h4 style={{ fontWeight: 'bold', color: '#333' }}>Keep Trying!</h4>
                                    <p className="text-muted mx-auto" style={{ maxWidth: '550px', fontSize: '15px', marginTop: '15px', lineHeight: '1.6' }}>
                                        You scored <strong>{score}%</strong>. A minimum score of 50% is required to pass this assessment and earn your certificate. Don't worry, you can review the material and try again at any time.
                                    </p>
                                    <button 
                                        className="btn btn_theme px-5 py-2 mt-4" 
                                        style={{ borderRadius: '5px', fontWeight: 'bold' }}
                                        onClick={() => navigate(`/quiz/${slug}/${encoded_id}`)}
                                    >
                                        <i className="fa fa-repeat me-2"></i> Retake Quiz Now
                                    </button>
                                </div>
                            )}

                        </div>
                    </div>

                </div>
            </div>
        </section>
    );
};

export default QuizResult;
